import SimpleSchema from 'simpl-schema';
import { map } from 'lodash';
import requiredIf from '../../modules/schema-conditional-required';
import SubtaskParameterSchema from './SubtaskParameterSchema';
import {
  ConnectionParametersSchemaMonitor,
  ConnectionParametersSchemaIn,
  ConnectionParametersSchemaOut
} from './ConnectionParametersSchema';

import { NODE_TYPES, STATUS_TYPES } from './taskConstants';

/**
 * A node for a task in the incoming tasklist.
 *
 * @typedef {object} NodeBaseFields
 * @property {number} x - The x value for the node.
 * @property {number} y - The y value for the node.
 * @property {string} id - The node id. UNIQUE.
 * @property {string} nodeLabel - A human readable name for the node.
 * @property {string} argName - The name of the function this node calls at backend.
 * @property {string} methodUrl - The url for the backend service.
 * @property {string} nodeType - The node type. One of [SUBTASK,CONNECTION].
 * @property {string} comments - The comments for the node (only not empty in SUBTASK).
 */

/**
 * A node for a task in the incoming tasklist.
 *
 * @typedef {object} NodeSchemaIn
 * @augments NodeBaseFields
 * @property {ConnectionParametersSchemaIn} [connectionParams] - The connection parameters (required for CONNECTION).
 * @property {SubtaskParameterSchema[]} [paramList] - The parameter list for a subtask (required for SUBTASK).
 */

/**
 * A node for a task to run.
 *
 * @typedef {object} NodeSchemaOut
 * @augments NodeBaseFields
 * @property {ConnectionParametersSchemaOut} [connectionParams] - The connection parameters (required for CONNECTION).
 * @property {SubtaskParameterSchema[]} [paramList] - The parameter list for a subtask (required for SUBTASK).
 */

/**
 * A node for a task to save, after running the task and when in the monitor.
 *
 * @typedef {object} NodeSchemaToSave
 * @augments NodeBaseFields
 * @property {string} [status] - The status for the node. One of [ERROR,CANCELED,DONE,INPROGRESS,PAUSED].
 * @property {number} [progress] - The progress of the running node.
 * @property {string} [startedAt] - The date when it started ISODATE.
 * @property {string} [finishedAt] - The date when it finished ISODATE.
 * @property {ConnectionParametersSchemaMonitor} [connectionParams] - The connection parameters (required for CONNECTION).
 * @property {SubtaskParameterSchema[]} [paramList] - The parameter list for a subtask (required for SUBTASK).
 */

/**
 * A node for a task in the monitor.
 *
 * @typedef {object} NodeSchemaMonitor
 * @augments NodeBaseFields
 * @property {string} status - The status for the node. One of [ERROR,CANCELED,DONE,INPROGRESS,PAUSED].
 * @property {number} progress - The progress of the running node.
 * @property {string} startedAt - The date when it started ISODATE.
 * @property {string} finishedAt - The date when it finished ISODATE.
 * @property {ConnectionParametersSchemaMonitor} [connectionParams] - The connection parameters (required for CONNECTION).
 * @property {SubtaskParameterSchema[]} [paramList] - The parameter list for a subtask (required for SUBTASK).
 */

export const NodeSchemaMonitor = new SimpleSchema({
  x: {
    type: Number,
    label: 'The x position for the node'
  },
  y: {
    type: Number,
    label: 'The y position for the node'
  },
  id: {
    type: String,
    label: 'The node id. unique for every the task'
  },
  nodeLabel: {
    type: String,
    label: 'A human readable name for the node'
  },
  argName: {
    type: String,
    label: 'The name of the function this node calls at backend.'
  },
  nodeType: {
    type: String,
    label: 'The node type',
    allowedValues: map(NODE_TYPES.properties, 'value')
  },
  status: {
    type: String,
    label: 'The status for the running subtask',
    allowedValues: map(STATUS_TYPES.properties, 'value')
  },
  progress: {
    type: Number,
    label: 'The progress of the running subtask.'
  },
  startedAt: {
    type: String,
    label: 'The date when it started. ISODATE',
    optional: true
  },
  finishedAt: {
    type: String,
    label: 'The date when it finished. ISODATE',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: STATUS_TYPES.DONE === this.siblingField('status').value,
        schemaInstance: this
      });
    }
  },
  methodUrl: {
    type: String,
    label: 'The url this method should call',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: NODE_TYPES.SUBTASK === this.siblingField('nodeType').value,
        schemaInstance: this
      });
    }
  },
  paramList: {
    type: Array,
    label: 'The parameter list for a subtask',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: NODE_TYPES.SUBTASK === this.siblingField('nodeType').value,
        schemaInstance: this
      });
    }
  },
  'paramList.$': {
    type: SubtaskParameterSchema,
    label: 'A parameter for a subtask'
  },
  connectionParams: {
    type: ConnectionParametersSchemaMonitor,
    label: 'The connection parameters',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: NODE_TYPES.CONNECTION === this.siblingField('nodeType').value,
        schemaInstance: this
      });
    }
  },
  comments: {
    type: String,
    label: 'The comments for the node',
    optional: true
  }
});

const NodeSchemaOut = NodeSchemaMonitor.omit(
  'status',
  'progress',
  'startedAt',
  'finishedAt',
  'connectionParams'
);

NodeSchemaOut.extend({
  connectionParams: {
    type: ConnectionParametersSchemaOut,
    label: 'The connection parameters',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: NODE_TYPES.CONNECTION === this.siblingField('nodeType').value,
        schemaInstance: this
      });
    }
  }
});

const NodeSchemaToSave = NodeSchemaMonitor.omit('status', 'progress', 'startedAt', 'finishedAt');

NodeSchemaToSave.extend({
  status: {
    type: String,
    label: 'The status for the running subtask',
    allowedValues: map(STATUS_TYPES.properties, 'value'),
    optional: true
  },
  progress: {
    type: Number,
    label: 'The progress of the running subtask.',
    optional: true
  },
  startedAt: {
    type: String,
    label: 'The date when it started. ISODATE',
    optional: true
  },
  finishedAt: {
    type: String,
    label: 'The date when it finished. ISODATE',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: STATUS_TYPES.DONE === this.siblingField('status').value,
        schemaInstance: this
      });
    }
  }
});

const NodeSchemaIn = NodeSchemaMonitor.omit(
  'status',
  'progress',
  'startedAt',
  'finishedAt',
  'connectionParams',
  'paramList'
);

NodeSchemaIn.extend({
  connectionParams: {
    type: ConnectionParametersSchemaIn,
    label: 'The connection parameters',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: NODE_TYPES.CONNECTION === this.siblingField('nodeType').value,
        schemaInstance: this
      });
    }
  },
  paramList: {
    type: Array,
    label: 'The parameter list for a subtask',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: NODE_TYPES.SUBTASK === this.siblingField('nodeType').value,
        schemaInstance: this
      });
    }
  },
  'paramList.$': {
    type: SubtaskParameterSchema,
    label: 'A parameter for a subtask'
  }
});

export { NodeSchemaIn, NodeSchemaOut, NodeSchemaToSave };
